import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { useField } from 'formik';
import { StateModel } from 'redux/reducers';
import { useSelector } from 'react-redux';
import { colorsTheme } from 'resources/theme/styled/colors';
import { RelationshipModel } from '.';
import { AssociationEntries } from 'enums/onboarding/crm';
import { RelationshipTemplate } from 'typings/application/relationship-template';
import { IDocumentAssociationInfo } from 'typings/documents/documents';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import { INTAKE_FORM_FIELDS_GRID_SIZES } from 'constants/grids';
import {
  getPlaceholderForSelect,
  generateFieldPlaceholderForAdditionalField,
} from '../utils';

// components
import FieldIssue from '../FieldIssue';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import AdditionalField, {
  AdditionalFieldFormItemWithNullableIdModel,
} from 'components/Forms/FormComponents/AdditionalField';
import {
  Button,
  Card,
  Col,
  FieldValidationRule,
  FormField,
  FormSelect,
  FormSelectProps,
  Row,
  SectionIntro,
  SelectOption,
} from '@ui';

interface IProps {
  index: number;
  fieldName: string;
  deleteCallback?: () => void;
}

const RelationshipForm = ({ index, fieldName, deleteCallback }: IProps) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const [field, , fieldHelpers] = useField<RelationshipModel>(fieldName);
  const { intakeForm } = useEntityIntakeFormContext();
  const currentContactId = useSelector<StateModel>(
    (state) => state.auth.contactData?._id,
  );

  const documentAssociation = useMemo<
    IDocumentAssociationInfo | undefined
  >(() => {
    return {
      id: currentContactId as string,
      type: AssociationEntries.Contact,
    };
  }, [currentContactId]);

  const availableTemplateOptions = useMemo<
    SelectOption<RelationshipTemplate>[]
  >(() => {
    if (!intakeForm?.metadata?.relationshipTemplates) {
      return [];
    }

    return intakeForm.metadata.relationshipTemplates.map((e) => {
      return {
        id: e._id,
        label: e.parentName,
        model: e,
      };
    });
  }, [intakeForm?.metadata?.relationshipTemplates]);

  const handleTemplateSelect = (
    newValue: string,
    option: SelectOption<RelationshipTemplate>,
  ) => {
    if (!option.model) {
      return;
    }

    const additionalFields: AdditionalFieldFormItemWithNullableIdModel[] =
      option.model.additionalFields
        .filter(
          ({ relatedTo }) => relatedTo === 'any' || relatedTo === 'contact',
        )
        .map((e) => ({
          name: e.name,
          type: e.type,
          value: undefined,
          relatedTo: e.relatedTo,
          options: {
            onboardingRequired: e.options.onboardingRequired,
            isForRegulatedOnly: e.options.isForRegulatedOnly,
            documentRequiresCertification:
              e.options.documentRequiresCertification,
            selectOptions: e.options.selectOptions.length
              ? e.options.selectOptions.map((e) => JSON.parse(e.valueJSON))
              : [],
            formDocument: e.options.formDocument,
          },
          id: null,
        }));

    fieldHelpers.setValue({
      ...field.value,
      template: newValue,
      additionalFields,
    });
  };

  const handleTemplateFieldClear = () => {
    fieldHelpers.setValue({ ...field.value, additionalFields: [] });
  };

  const renderAdditionalForm = (
    additionalFields: AdditionalFieldFormItemWithNullableIdModel[],
    relationshipId: string,
  ) => {
    return additionalFields.map((ad, i) => (
      <Col key={i} {...INTAKE_FORM_FIELDS_GRID_SIZES}>
        <FormField
          label={
            <FieldIssue
              fieldId={`applicantRelations["${relationshipId}"].additionalFields["${ad.id}"].valueJSON`}
            >
              {ad.name}
            </FieldIssue>
          }
          name={`${fieldName}.additionalFields.${i}.value`}
          component={AdditionalField}
          additionalProps={{
            renderPlaceholder: (validationRules: FieldValidationRule[]) =>
              generateFieldPlaceholderForAdditionalField(
                validationRules || [],
                ad,
                t,
              ),
            field: {
              type: ad.type,
              options: ad.options.selectOptions,
              formDocument: ad.options.formDocument,
              documentAssociation,
            },
          }}
        />
      </Col>
    ));
  };

  return (
    <StyledCard>
      <SectionIntro
        hideDivider
        titleColor={colorsTheme.colorWhite}
        title={t('intake_form.basic_info.relationship_title', {
          index: index + 1,
        })}
        titleVariant="h5"
      />
      <Row wrap={true} justify="start">
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormSelectProps<RelationshipTemplate>>
            label={
              <FieldIssue
                fieldId={`applicantRelations["${field.value.id}"].relationshipTemplateId`}
              >
                {t('intake_form.basic_info.relationship_template')}
              </FieldIssue>
            }
            name={`${fieldName}.template`}
            component={FormSelect}
            additionalProps={{
              options: availableTemplateOptions,
              onSelect: handleTemplateSelect,
              onClear: handleTemplateFieldClear,
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        {field.value && field.value.additionalFields.length
          ? renderAdditionalForm(field.value.additionalFields, field.value.id)
          : null}
      </Row>
      {deleteCallback ? (
        <HideIfDisabledForm hideIfSubmitting>
          <Button size="large" onClick={deleteCallback}>
            {t('remove', { ns: 'common' })}
          </Button>
        </HideIfDisabledForm>
      ) : null}
    </StyledCard>
  );
};

const StyledCard = styled(Card)`
  .ant-card-body {
    min-height: 0px;
    padding: ${({ theme }) => theme.paddingXl};
  }
`;

export default RelationshipForm;
